import type { Lap, Session } from '../xrk/types'
import { cumulativeDistance, findSpeedChannel, lowerBound, sampleAt } from './laps'

/** Lap distance may differ from the median full lap by this fraction before it is flagged. */
const DISTANCE_TOLERANCE = 0.1
/** Lap time above this multiple of the median full lap is a pit, red-flag or cool-down lap. */
const SLOW_FACTOR = 1.4
/** A lap must start and end within this many metres of the start/finish point. */
const SF_RADIUS_M = 40

/**
 * Decide which laps are complete, flying laps. Runs after decoding.
 *
 * Checks, in order: the logger's own out/in marking, a start/finish proximity test at both
 * ends of the lap, then distance and time against the median of the remaining laps.
 * A session always keeps at least one valid lap so there is a reference to compare against.
 */
export function classifyLaps(session: Session): Session {
  if (session.laps.length === 0) return session
  const laps: Lap[] = session.laps.map((l) => ({ ...l }))

  // Lap distances from integrated GPS speed.
  const speed = findSpeedChannel(session)
  const lapDistance = new Map<number, number>()
  if (speed && speed.time.length > 1) {
    const dist = cumulativeDistance(speed.time, speed.values)
    for (const l of laps) {
      lapDistance.set(l.index, sampleAt(speed.time, dist, l.end) - sampleAt(speed.time, dist, l.start))
    }
  }

  // Start/finish proximity: a flying lap starts and ends at the line. The line is where the
  // logger split the laps. The S/F point in the file header is that line when lap boundaries
  // land on it; a track record can carry a different line than the one the logger timed with,
  // so a header point that no boundary agrees with is set aside for the median of the
  // boundaries' positions. The header point goes first because a stretch of misplaced GPS can
  // move most of the boundaries, and the median would then fail the laps that are right.
  if (session.gps.length > 1) {
    const gpsTime = session.gps.map((g) => g.time)
    const posAt = (t: number): { lat: number; lon: number } | null => {
      const i = lowerBound(gpsTime, t)
      const b = session.gps[Math.min(i, session.gps.length - 1)]
      const a = session.gps[Math.max(0, i - 1)]
      // A GPS gap around the boundary: no opinion rather than a wrong one.
      if (Math.min(Math.abs(a.time - t), Math.abs(b.time - t)) > 2) return null
      const f = b.time === a.time ? 0 : Math.max(0, Math.min(1, (t - a.time) / (b.time - a.time)))
      return { lat: a.lat + f * (b.lat - a.lat), lon: a.lon + f * (b.lon - a.lon) }
    }
    const boundaries = laps
      .slice(1)
      .map((l) => posAt(l.start))
      .filter((p): p is { lat: number; lon: number } => p != null)
    let line = session.meta.startFinish
    if (boundaries.length) {
      const header = line
      const agreeing = header
        ? boundaries.filter((p) => haversine(p.lat, p.lon, header.lat, header.lon) <= SF_RADIUS_M).length
        : 0
      // Two boundaries on the header point (or the only one there is) are no coincidence.
      if (agreeing < Math.min(2, boundaries.length))
        line = { lat: median(boundaries.map((p) => p.lat)), lon: median(boundaries.map((p) => p.lon)) }
    }
    if (line) {
      const far = (t: number) => {
        const p = posAt(t)
        return p != null && haversine(p.lat, p.lon, line.lat, line.lon) > SF_RADIUS_M
      }
      for (const l of laps) {
        if (l.incomplete) continue
        if (far(l.start) || far(l.end)) {
          l.incomplete = true
          l.reason = 'no S/F crossing'
        }
      }
    }
  }

  // Distance and time against the median of the laps still considered complete.
  let candidates = laps.filter((l) => !l.incomplete)
  if (candidates.length >= 2) {
    const medD = median(candidates.map((l) => lapDistance.get(l.index) ?? NaN).filter(Number.isFinite))
    const medT = median(candidates.map((l) => l.duration))
    for (const l of candidates) {
      const d = lapDistance.get(l.index)
      if (Number.isFinite(medD) && d != null && Math.abs(d - medD) / medD > DISTANCE_TOLERANCE) {
        l.incomplete = true
        l.reason = d < medD ? 'short lap' : 'long lap'
      } else if (l.duration > SLOW_FACTOR * medT) {
        l.incomplete = true
        l.reason = 'slow lap'
      }
    }
  }

  // Guarantee a reference lap: keep the most complete one if everything was flagged.
  candidates = laps.filter((l) => !l.incomplete)
  if (candidates.length === 0) {
    const pick = laps.reduce((best, l) => {
      const score = (x: Lap) => (x.reason === 'long lap' ? -1 : (lapDistance.get(x.index) ?? x.duration))
      return score(l) > score(best) ? l : best
    })
    pick.incomplete = false
    pick.reason = undefined
  }

  return { ...session, laps }
}

function median(xs: number[]): number {
  if (xs.length === 0) return NaN
  const s = [...xs].sort((a, b) => a - b)
  const mid = s.length >> 1
  return s.length % 2 ? s[mid] : 0.5 * (s[mid - 1] + s[mid])
}

/** Great-circle distance in metres. */
export function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}
